import { OPR_SESSION_TOKEN } from "@/constants";
import request, { _GET, _POST } from "@/services/request";
import { message } from "antd";
import {
  BatchShelfProductReq,
  DakeBatchOptProductReq,
  DakeBatchOptProductRes,
  DakeBatchOptProductRes1,
  DakeListChannelsReq,
  DakeSelectionListReq,
  DakeSelectionListRsp,
  GoodsLibraryLogResponse,
  GoodsLibraryRequest,
  GoodsLibraryResponse,
  InstitutionsSettleListResult,
  PlanListRequest,
  PlanListResponse,
  ProductDetailData,
} from "@/data-contracts";

export interface ProductConfigImportResultItem {
  productId: string;
  title: string;
  headSupplierItemLink: string;
  retainedRatio: number;
  success: boolean;
  failedReason?: string;
}

export interface ProductConfigImportResult {
  results: ProductConfigImportResultItem[];
}

/**
 *
 * 推广计划记录列表
 *
 * /admin/plan/list
 *
 * get
 *
 * @param params PlanListRequest
 * */
export const getSelectionList = (params?: PlanListRequest) =>
  request<PlanListResponse, PlanListRequest>(_GET, "/admin/plan/list")(params ?? {});

/**
 *
 * 结束推广计划
 *
 * /admin/plan/end
 *
 * @param params {id: string}
 *
 * @return null
 *
 * */
export const endSelection = (params: { id: string }) => request<any, { id: string }>(_POST, "/admin/plan/end")(params);

/**
 *
 * 获取商品列表
 *
 * /admin/product/list
 *
 * post
 *
 * @param params GoodsLibraryRequest
 *
 * @return GoodsLibraryResponse
 *
 * */
export const getGoodsLibrary = (params?: GoodsLibraryRequest) =>
  request<GoodsLibraryResponse, GoodsLibraryRequest>(_POST, "/admin/product/list")(params ?? {});

/**
 *
 * 导入商品选品服务费列表
 *
 * /admin/product/retain_ratio_list/import
 *
 * post multipart
 *
 * */
export const importProductConfig = (file: File) => {
  const formData = new FormData();
  formData.append("fileName", file);

  return request<ProductConfigImportResult, FormData>(_POST, "/admin/product/retain_ratio_list/import", {
    "Content-Type": "multipart/form-data",
  })(formData);
};

function parseContentDispositionFilename(header?: string | null): string | undefined {
  if (!header) return undefined;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const match = header.match(/filename="?([^";\n]+)"?/i);
  return match?.[1]?.trim();
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 *
 * 下载商品选品服务费导入模板
 *
 * /admin/product/retain_ratio_list/export
 *
 * post
 *
 * */
export const downloadProductConfigTemplate = async (headSupplierItemLinks: string[]) => {
  try {
    const res = await fetch((process.env.ENV_REPLACE_API_URL || "") + "/admin/product/retain_ratio_list/export", {
      method: _POST,
      headers: {
        "Content-Type": "application/json",
        Authorization: window.localStorage.getItem(OPR_SESSION_TOKEN) || "",
      },
      body: JSON.stringify({ headSupplierItemLinks }),
    });

    if (!res.ok) {
      throw new Error("下载模板失败");
    }

    const blob = await res.blob();
    const filename =
      parseContentDispositionFilename(res.headers.get("content-disposition")) ?? "商品选品服务费导入模板.xlsx";
    downloadBlob(blob, filename);
  } catch (error: any) {
    message.error(error?.data?.msg ?? error?.message ?? "下载模板失败");
    return Promise.reject(error);
  }
};

/**
 *
 * 获取商品库操作日志
 *
 * /admin/product/listOptLog
 *
 * @param params {productId: string}
 * */
export const getGoodsLibraryOptLog = (params: { headSupplierItemLink: string }) =>
  request<GoodsLibraryLogResponse, { headSupplierItemLink: string }>(_POST, "/admin/product/listOptLog")(params);

/**
 *
 * 获取计划操作日志
 *
 * /admin/plan/listOptLog
 *
 * /admin/product/listOptLog
 *
 * @param params {productId: string}
 * */
export const getPlanOptLog = (params: { planId: string }) =>
  request<GoodsLibraryLogResponse, { planId: string }>(_POST, "/admin/plan/listOptLog")(params);

/**
 *
 * 获取待上架选品列表
 *
 * /admin/selection/list
 *
 * Post
 *
 * @param params DakeSelectionListReq
 *
 * @return DakeSelectionListRsp
 * */
export const getPendingShelfSelectionList = (params?: DakeSelectionListReq) =>
  request<DakeSelectionListRsp, DakeSelectionListReq>(_POST, "/admin/selection/list")(params ?? {});

/**
 *
 * 批量操作商品
 *
 * /admin/product/batchOpt
 *
 * @param params DakeBatchOptProductRes1
 * */
export const batchOptProduct = (params: DakeBatchOptProductReq) =>
  request<DakeBatchOptProductRes1, DakeBatchOptProductReq>(_POST, "/admin/product/batchOpt")(params);

/**
 *
 * 获取推广机构/达人
 *
 * /admin/product/ListPromotedChannel
 *
 * @param params DakeListChannelsReq
 * */
export const listPromotedChannel = (params: DakeListChannelsReq) =>
  request<InstitutionsSettleListResult, DakeListChannelsReq>(_POST, "/admin/product/ListPromotedChannel")(params);

/**
 * 批量添加商品
 *
 * /admin/selection/batchAddProduct
 *
 * @param params BatchShelfProductReq
 *
 * */
export const batchAddProduct = (params: BatchShelfProductReq) =>
  request<DakeBatchOptProductRes, BatchShelfProductReq>(_POST, "/admin/selection/batchAddProduct")(params);

/**
 *
 * 商品详情
 * /admin/product/detail
 *
 * @param params {productId: string}
 *
 * @return ProductDetailData
 * */
export const getProductDetail = (params: { headSupplierItemLink: string }) =>
  request<ProductDetailData, { headSupplierItemLink: string }>(_POST, "/admin/product/detail")(params);

const selection = {
  getSelectionList,
  getPendingShelfSelectionList,
  listPromotedChannel,
  batchOptProduct,
  getGoodsLibrary,
  importProductConfig,
  downloadProductConfigTemplate,
  getGoodsLibraryOptLog,
  getPlanOptLog,
  batchAddProduct,
  getProductDetail,
  endSelection,
};

export default selection;
